// Angular Imports
import { ActivatedRoute } from '@angular/router';
import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

// CC-Common Imports
import { IconConstants, Utils } from 'cc-common';

// 3rd party imports
import { Subscription } from 'rxjs';

// Constant imports
import { ImageConstants } from '../../_constants/image-constants';

// Service imports
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { WorkQueueActionsService } from '../_services/work-queue-actions.service';

// Model imports
import { WorkQueueActionParametersDto } from '../_models/work-queue-action-parameters-dto';
import { CorpAPWorkQueueDetailResultDto } from '../_models/corp-ap-work-queue-detail-result-dto';
import { CorpAPWorkQueueEmailActionParameterDto } from '../_models/corp-ap-work-queue-email-action-parameter-dto';
import { CorpAPWorkQueueEmailActionDenyParameterDto } from '../_models/corp-ap-work-queue-email-action-deny-parameter-dto';

@Component({
    selector: 'app-work-queue-actions',
    templateUrl: './work-queue-actions.component.html',
    styleUrls: ['./work-queue-actions.component.scss']
})
export class WorkQueueActionsComponent implements OnInit {
    workQueueActionParams = new WorkQueueActionParametersDto();
    workQueueActionsDetailResult: CorpAPWorkQueueDetailResultDto;
    ciraLogo = ImageConstants.ccLogo;
    pageTitle = 'Work Queue Approval';

    loaderSubscription: Subscription;
    loadingVisible = false;
    action = '';
    reason = '';
    status: any;

    iconCheck = IconConstants.CHECK;
    iconCross = IconConstants.CROSS;
    iconWarn = IconConstants.WARN;
    iconFlag = IconConstants.FLAG;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly errorService: ServerErrorService,
        private readonly workQueueActionsService: WorkQueueActionsService
    ) {
        this.onSubmit = this.onSubmit.bind(this);
    }

    ngOnInit(): void {
        this.loaderSubscription = this.workQueueActionsService.onLoadingEvent().subscribe((showLoading: boolean) => {
            setTimeout(() => (this.loadingVisible = showLoading), 10);
        });
        this.route.queryParamMap.subscribe((data) => {
            const params = data['params'];
            if (Utils.notNullOrEmpty(params)) {
                this.workQueueActionParams.workQueueId = params['id'];
                this.workQueueActionParams.action = params['ac'];
                this.workQueueActionParams.userId = params['uid'];
                this.workQueueActionParams.emailJobId = params['batchJobId'];
                this.populateWorkQueueActionsDetail();
            }
        });
    }

    onSubmit(): void {
        if (Utils.isTrue(this.workQueueActionsDetailResult.allowDeny)) {
            if (Utils.isNullOrEmpty(this.reason)) {
                this.errorService.updateSubjectErrorMessage('Please provide a deny reason');
            } else {
                this.submitWorkQueueDenyAction();
            }
        }
    }

    private populateWorkQueueActionsDetail(): void {
        this.workQueueActionsService.showLoading();
        this.workQueueActionsService.getWorkQueueActionsDetail(this.workQueueActionParams).subscribe(
            (result: CorpAPWorkQueueDetailResultDto) => {
                this.workQueueActionsDetailResult = result;
                this.workQueueActionsService.hideLoading();
                this.action = Utils.isTrue(this.workQueueActionsDetailResult.allowApprove) ? 'approved' : 'denied';
                if (Utils.isTrue(result.allowApprove)) {
                    this.submitWorkQueueApproveAction();
                }
            },
            (error) => {
                this.workQueueActionsService.hideLoading();
                this.errorHandler(error);
            }
        );
    }

    private submitWorkQueueApproveAction(): void {
        const params = new CorpAPWorkQueueEmailActionParameterDto();
        params.emailJobId = this.workQueueActionParams.emailJobId;
        params.userId = this.workQueueActionParams.userId;
        params.workQueueId = this.workQueueActionParams.workQueueId;

        this.workQueueActionsService.showLoading();
        this.workQueueActionsService.approveWorkQueueAction(params).subscribe(
            () => {
                this.workQueueActionsService.hideLoading();
                this.successResponseHandler();
            },
            (error) => {
                this.workQueueActionsService.hideLoading();
                this.errorService.displayError(error);
            }
        );
    }

    private submitWorkQueueDenyAction(): void {
        const params = new CorpAPWorkQueueEmailActionDenyParameterDto();
        params.emailJobId = this.workQueueActionParams.emailJobId;
        params.userId = this.workQueueActionParams.userId;
        params.workQueueId = this.workQueueActionParams.workQueueId;
        params.denyReason = this.reason;

        this.workQueueActionsService.showLoading();
        this.workQueueActionsService.denyWorkQueueAction(params).subscribe(
            () => {
                this.workQueueActionsService.hideLoading();
                this.successResponseHandler();
            },
            (error) => {
                this.workQueueActionsService.hideLoading();
                this.errorService.displayError(error);
            }
        );
    }

    private successResponseHandler(): void {
        const success200 = new Object();
        success200['iconText'] = 'SUCCESS';
        success200['statusHeader'] = 'Success';
        success200[
            'statusMsg'
        ] = `Work Queue #${this.workQueueActionsDetailResult.workQueue.workQueueId} has been successfully ${this.action}.`;
        this.status = success200;
    }

    private errorHandler(error: HttpErrorResponse): void {
        if (Utils.equals(error.status, 409)) {
            this.processErrorMsg(error.error);
        } else {
            this.errorService.displayError(error);
        }
    }

    private processErrorMsg(error: string): void {
        const error409 = new Object();
        const errorList = Utils.equalsIgnoreCase(typeof error, 'string') && error.includes('|') ? error.split('|') : [];
        if (errorList.length === 3) {
            error409['iconText'] = errorList[0];
            error409['statusHeader'] = errorList[1];
            error409['statusMsg'] = errorList[2];
            this.status = error409;
        }
    }
}
